import Vuex from "vuex";
import isMobile from "ismobilejs";
import diario from "./modules/diario";
import contato from "./modules/contato";
import usuario from "./modules/usuario";
import * as types from "./mutation-types";
import aparicoes from "./modules/aparicoes";
import assinaturaApi from "../api/assinatura";
import monitoramento from "./modules/monitoramento";
import solicitacaoAutos from "./modules/solicitacao-autos";
import * as Sentry from "@sentry/browser";

const debug = process.env.NODE_ENV !== "production";

const state = {
  selecionado: [],
  planos: [],
  loading: {
    show: false,
    bar: true,
    icon: true,
    type: "loading",
    message: "Processando...",
    closing: false
  },
  modals: {
    login: false,
    cadastro: false,
    contato: false,
    assinatura: false,
    termoUso: false,
    confirmarEmail: false,
    editarMonitoramento: false,
    selecionarPerfil: false,
    sugerirProcessos: false,
    nps: false,
    novoMonitoramento: false
  },
  isMenuOpen: false,
  loadingTimeout: null,
  ismobile: isMobile.any,
  routes: [],
  iconsPath: null,
  loadingIconsPath: false
};

const getters = {
  mobilePlatform: () => {
    if (isMobile.android && isMobile.android.device) {
      return "android";
    } else if (isMobile.apple && isMobile.apple.device) {
      return "ios";
    } else {
      return "other";
    }
  }
};

const actions = {
  getPlanos({ commit }) {
    if (state.planos.length) {
      return Promise.resolve();
    }
    return assinaturaApi.getPlanos().then(({ data }) => {
      commit(types.SET_PLANOS, data.data);
    });
  },
  hideLoading({ commit }, checkIfHasMessage = false) {
    if (checkIfHasMessage) {
      return new Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      commit(types.CLOSE_LOADING);
      const timeout = window.setTimeout(() => {
        commit(types.HIDE_LOADING);
        resolve();
      }, 300);
      commit(types.SET_LOADING_TIMEOUT, timeout);
    });
  },
  showLoadingMessage({ commit, dispatch }, config) {
    if (state.loadingTimeout !== null) {
      window.clearTimeout(state.loadingTimeout);
      commit(types.RESET_LOADING_TIMEOUT);
    }
    if (state.loading.show) {
      dispatch("hideLoading").then(() => {
        commit(types.SHOW_LOADING_MESSAGE, config);
      });
    } else {
      commit(types.SHOW_LOADING_MESSAGE, config);
    }
    const timeout = window.setTimeout(() => dispatch("hideLoading"), config?.timeout || 4000);
    commit(types.SET_LOADING_TIMEOUT, timeout);
  },
  showLoading({ commit, dispatch }, config) {
    if (state.loadingTimeout !== null) {
      window.clearTimeout(state.loadingTimeout);
      commit(types.RESET_LOADING_TIMEOUT);
    }
    if (state.loading.show) {
      dispatch("hideLoading").then(() => {
        commit(types.SHOW_LOADING, config);
      });
    } else {
      commit(types.SHOW_LOADING, config);
    }
  },
  /**
   *
   * @param commit
   * @param dispatch
   * @param {int} httpStatus
   * @param {array} responseData array de mensagens
   * @param exception
   */
  showHttpErrorMessage(
    { commit, dispatch },
    { httpStatus, responseData, exception }
  ) {
    let messages = [];
    let errors;

    if (
      typeof responseData === "object" &&
      responseData !== null &&
      !responseData.errors &&
      httpStatus === 422
    ) {
      errors = Object.keys(responseData).map(k => responseData[k][0]);
    } else if (typeof responseData !== "undefined" && responseData.errors) {
      errors = responseData.errors;
    } else {
      errors = ["Ocorreu um erro"];

      if (typeof exception !== "undefined") {
        Sentry.captureException(exception);
      }
    }

    for (const key in errors) {
      messages.push(errors[key]);
    }

    dispatch("showLoadingMessage", {
      message: messages[0],
      type: "danger"
    });
  },
  fetchIconsPath({ commit }) {
    if (state.iconsPath || state.loadingIconsPath) {
      return Promise.resolve();
    }
    commit(types.LOADING_ICONS, true);
    return axios
      .get("/mix-manifest.json")
      .then(({ data }) => {
        commit(types.SET_ICONS_PATH, data);
        commit(types.LOADING_ICONS, false);
      })
      .catch(() => commit(types.LOADING_ICONS, false));
  }
};

const mutations = {
  [types.LOADING_ICONS](state, status) {
    state.loadingIconsPath = status;
  },
  [types.SET_ICONS_PATH](state, icons) {
    state.iconsPath = icons;
  },
  [types.SHOW_LOADING](state, { type, message }) {
    state.loading.bar = true;
    state.loading.icon = true;
    state.loading.show = true;
    state.loading.type = type || "loading";
    state.loading.message = message || "Processando...";
    state.loading.closing = false;
  },
  [types.SHOW_LOADING_MESSAGE](state, { type, message }) {
    state.loading.bar = false;
    state.loading.icon = false;
    state.loading.show = true;
    state.loading.type = type || "done";
    state.loading.message = message;
    state.loading.closing = false;
  },
  [types.HIDE_LOADING](state) {
    state.loading.show = false;
    state.loading.type = "done";
    state.loading.message = "Processando...";
    state.loading.closing = false;
  },
  [types.CLOSE_LOADING](state) {
    state.loading.closing = true;
  },
  [types.SET_LOADING_TIMEOUT](state, timeout) {
    state.loadingTimeout = timeout;
  },
  [types.RESET_LOADING_TIMEOUT](state) {
    state.loadingTimeout = null;
  },
  [types.TOGGLE_MENU](state, status) {
    if (typeof status !== "undefined") {
      state.isMenuOpen = status;
    } else {
      state.isMenuOpen = !state.isMenuOpen;
    }
  },
  [types.TOGGLE_MODAL](state, modal) {
    state.modals[modal] = !state.modals[modal];
  },
  [types.SET_ITENS_SELECIONADO](state, itens) {
    state.selecionado = itens;
  },
  [types.SET_ITEM_SELECIONADO](state, item) {
    state.selecionado.push(item);
  },
  [types.SET_PLANOS](state, planos) {
    state.planos = planos;
  },
  [types.ROUTER_PUSH](state, routeName) {
    state.routes.push(routeName);
  },
  [types.ROUTER_POP](state, routeName) {
    state.routes.pop();
  }
};

export default new Vuex.Store({
  modules: {
    monitoramento,
    aparicoes,
    usuario,
    diario,
    solicitacaoAutos,
    contato
  },
  state,
  getters,
  mutations,
  actions,
  // In strict mode, whenever Vuex state is mutated outside of mutation handlers
  // an error will be thrown
  strict: debug // Do not enable strict mode when deploying for production!
});
